<template>
  <div v-if="shouldShowButton" class="chat-root">
    <!-- Popup d'information unique (tour) -->
    <transition name="fade">
      <div v-if="showChannelInfoPopup" class="channel-info-popup-overlay">
        <div class="channel-info-popup">
          <div class="popup-header">
            <i class="fas fa-comments"></i>
            <h3>Welcome to PettyCash Chat</h3>
          </div>
          <div class="popup-body">
            <p>You have two ways to get help:</p>
            <div class="popup-channel">
              <div class="popup-channel-icon support"><i class="fas fa-headset"></i></div>
              <div class="popup-channel-text">
                <strong>PettyCash Support</strong><br>
                General help, transactions, account issues.
              </div>
            </div>
            <div class="popup-channel">
              <div class="popup-channel-icon sponsor"><i class="fas fa-user-tie"></i></div>
              <div class="popup-channel-text">
                <strong>Sponsor Chat</strong><br>
                Talk to your referrer (if you have one).
              </div>
            </div>
            <p class="popup-note">You can switch channels later using the navigation bar.</p>
          </div>
          <div class="popup-footer">
            <button class="popup-btn primary" @click="closeChannelInfoPopup(true)">Got it! Start chatting</button>
          </div>
        </div>
      </div>
    </transition>

    <!-- ═══════════════════════════════════════════════════════════════
         STATE 1: CLOSED — Floating button + invitation bubble
         ═══════════════════════════════════════════════════════════════ -->
    <div class="chat-closed-state" :class="{ mobile: isMobile }" v-if="!isOpen">
      <!-- Invitation bubble -->
      <transition name="bubble-slide">
        <div
          v-if="showInvitationBubble"
          class="invitation-bubble"
          @click="openChat"
          @touchstart="handleBubbleTouchStart"
          @touchend="handleBubbleTouchEnd"
        >
          <button class="bubble-close" @click.stop="dismissBubble">

          </button>


            👋 <span class="highlight">Chat With Us</span>

        </div>
      </transition>

      <!-- Main floating action button -->
      <button class="main-fab" @click="openChat" :class="{ 'has-unread': totalUnreadCount > 0 }">
        <i class="fas fa-comment-dots"></i>
        <span v-if="totalUnreadCount > 0" class="fab-badge">{{ totalUnreadCount > 9 ? '9+' : totalUnreadCount }}</span>
        <span class="fab-pulse-ring" v-if="totalUnreadCount > 0"></span>
      </button>

      <!-- Message count label -->
      <transition name="fade">
        <span v-if="totalUnreadCount > 0" class="fab-message-label">
          {{ totalUnreadCount }} new message{{ totalUnreadCount > 1 ? 's' : '' }}
        </span>
      </transition>
    </div>

    <!-- ═══════════════════════════════════════════════════════════════
         STATE 3: SIDEBAR CHAT WINDOW (Support or Sponsor)
         ═══════════════════════════════════════════════════════════════ -->
    <transition name="window-slide">
      <div
        v-if="isOpen && activeChannel"
        class="chat-window-overlay"
        :class="{ mobile: isMobile, 'keyboard-open': keyboardHeight > 0 }"
        :style="overlayStyle"
      >
        <div class="sidebar-chat-window" :class="{ 'chatbot-fullscreen': isFullscreen, mobile: isMobile }">

          <!-- ═══════ LEFT SIDEBAR (desktop uniquement) ═══════ -->
          <div class="chat-sidebar" v-if="!isMobile">
            <div class="sidebar-logo" title="PettyCash Chat">
              <i class="fas fa-comments"></i>
            </div>
            <div class="sidebar-divider"></div>

            <!-- Support Channel Button -->
            <button
              class="sidebar-channel-btn"
              :class="{ active: activeChannel === 'support' }"
              @click="switchChannel('support')"
              title="PettyCash Support"
            >
              <i class="fas fa-headset"></i>
              <span v-if="supportUnreadCount > 0" class="sidebar-channel-badge">{{ supportUnreadCount }}</span>
              <span class="sidebar-tooltip">Support</span>
            </button>

            <!-- Sponsor Channel Button -->
            <button
              class="sidebar-channel-btn"
              :class="{ active: activeChannel === 'sponsor', disabled: !hasSponsor }"
              @click="hasSponsor && switchChannel('sponsor')"
              title="Sponsor Chat"
            >
              <i class="fas fa-user-tie"></i>
              <span v-if="hasSponsor" class="sidebar-channel-status" :class="sponsorStatus"></span>
              <span v-if="sponsorUnreadCount > 0" class="sidebar-channel-badge">{{ sponsorUnreadCount }}</span>
              <span class="sidebar-tooltip">Sponsor</span>
            </button>

            <!-- Bottom actions -->
            <div class="sidebar-bottom">
              <div class="sidebar-divider"></div>
              <button class="sidebar-settings-btn" @click="backToMenu" title="Back to menu">
                <i class="fas fa-arrow-left"></i>
                <span class="sidebar-tooltip">Menu</span>
              </button>
              <button class="sidebar-settings-btn" @click="closeChat" title="Close chat">
                <i class="fas fa-times"></i>
                <span class="sidebar-tooltip">Close</span>
              </button>
              <button class="sidebar-settings-btn" @click="toggleFullscreen" title="Toggle fullscreen">
                <i class="fas" :class="isFullscreen ? 'fa-compress' : 'fa-expand'"></i>
                <span class="sidebar-tooltip">{{ isFullscreen ? 'Shrink' : 'Expand' }}</span>
              </button>
            </div>
          </div>

          <!-- ═══════ CHAT MAIN AREA ═══════ -->
          <div class="chat-main">
            <!-- Header — les actions sont maintenant à l'intérieur -->
            <div class="chat-header" :class="{ 'sponsor-theme': activeChannel === 'sponsor', mobile: isMobile }">
              <!-- Flèche retour mobile → ferme le chat -->
              <button v-if="isMobile" class="header-back-btn" @click="closeChat">
                <i class="fas fa-arrow-left"></i>
              </button>
              <div class="chat-header-avatar" :class="{ sponsor: activeChannel === 'sponsor' }">
                <i :class="activeChannel === 'support' ? 'fas fa-headset' : 'fas fa-user-tie'"></i>
              </div>
              <div class="chat-header-info">
                <h4>
                  {{ activeChannel === 'support' ? 'PettyCash Support' : sponsorName }}
                  <span v-if="activeChannel === 'sponsor'" class="sponsor-label">Your Sponsor</span>
                </h4>
                <span v-if="activeChannel === 'support'" class="status-online">
                  <span class="status-pulse"></span> Online
                </span>
                <span v-else :class="['status-text', sponsorStatus]">
                  <span class="status-pulse" :class="sponsorStatus"></span>
                  {{ sponsorStatus === 'online' ? 'Online' : sponsorStatus === 'away' ? 'Away' : 'Offline' }}
                </span>
              </div>
              <!-- Bouton fermer — desktop seulement, sur mobile c'est la flèche ← -->
              <div v-if="!isMobile" class="chat-header-actions">
                <button class="header-action-btn" @click="closeChat">
                  <i class="fas fa-times"></i>
                </button>
              </div>
            </div>

            <!-- Messages Area -->
            <div
              ref="messagesContainer"
              class="chat-messages"
              @scroll="handleScroll"
              @dragover.prevent="onDragOver"
              @dragleave.prevent="onDragLeave"
              @drop.prevent="onDrop"
            >
              <!-- Notification de nouveaux messages -->
              <transition name="fade">
                <div v-if="showNewMessageNotification && newMessageCount > 0" class="new-message-notification" @click="scrollToBottom(true)">
                  <i class="fas fa-arrow-down"></i>
                  <span>{{ newMessageCount }} new message{{ newMessageCount > 1 ? 's' : '' }}</span>
                </div>
              </transition>

              <!-- Bouton "Earlier messages" (en haut) -->
              <transition name="bounce">
                <div
                  v-if="!isAtTop && activeMessages.length > 0"
                  class="scroll-to-top"
                  @click="scrollToTop"
                >
                  <i class="fas fa-arrow-up"></i>
                  <span>Earlier messages</span>
                </div>
              </transition>

              <!-- File drop overlay -->
              <transition name="fade">
                <div v-if="isDraggingFile" class="file-drop-overlay">
                  <div class="file-drop-content">
                    <i class="fas fa-cloud-upload-alt fa-3x"></i>
                    <span>Drop file here to send</span>
                  </div>
                </div>
              </transition>

              <!-- Loading state -->
              <div v-if="isLoadingHistory" class="loading-state">
                <div class="loading-spinner"></div>
                <p>Loading messages...</p>
              </div>

              <!-- Empty state -->
              <div v-else-if="activeMessages.length === 0" class="empty-state">
                <div class="empty-avatar" :class="{ sponsor: activeChannel === 'sponsor' }">
                  <i :class="activeChannel === 'support' ? 'fas fa-headset fa-2x' : 'fas fa-user-tie fa-2x'"></i>
                </div>
                <h3>{{ activeChannel === 'support' ? 'PettyCash Support' : sponsorName }}</h3>
                <p>{{ activeChannel === 'support' ? 'How can we help you today?' : 'Start a conversation with your sponsor' }}</p>
                <div v-if="activeChannel === 'support'" class="quick-suggestions">
                  <button
                    v-for="(suggestion, index) in quickSuggestions"
                    :key="index"
                    @click="sendMessage(suggestion.text)"
                    class="suggestion-chip"
                  >
                    <i :class="suggestion.icon"></i>
                    <span>{{ suggestion.text }}</span>
                  </button>
                </div>
              </div>

              <!-- Messages list -->
              <template v-else>
                <div
                  v-for="(msg, index) in activeMessages"
                  :key="`${msg.id || msg.timestamp}-${index}`"
                  class="message-group"
                  :class="{ 'message-new': msg.isNew }"
                  @dblclick="handleMessageDoubleTap(msg)"
                >
                  <!-- Date separator -->
                  <div v-if="showDateSeparator(msg, index)" class="date-separator">
                    <span>{{ formatDateSeparator(msg.timestamp) }}</span>
                  </div>

                  <div :class="['message-row', msg.is_mine ? 'sent' : 'received']">
                    <div class="msg-avatar-small" :class="{ sponsor: !msg.is_mine && activeChannel === 'sponsor' }">
                      <i :class="msg.is_mine ? 'fas fa-user' : (activeChannel === 'support' ? 'fas fa-headset' : 'fas fa-user-tie')"></i>
                    </div>
                    <div class="msg-bubble" :class="{ 'sponsor-bubble': !msg.is_mine && activeChannel === 'sponsor', 'has-products': msg.products && msg.products.length }">
                      <!-- Text message -->
                      <div v-if="msg.message_type === 'text' || !msg.message_type" class="msg-bubble-text" v-html="formatMessage(msg.content)"></div>

                      <!-- Image -->
                      <div v-else-if="msg.message_type === 'image'" class="media-message">
                        <img
                          v-if="!imageErrors[msg.file_url]"
                          :src="msg.file_url"
                          class="msg-image"
                          @click="openImageModal(msg.file_url)"
                          @error="handleImageError(msg.file_url)"
                          loading="lazy"
                        />
                        <div v-else class="media-error">
                          <i class="fas fa-image"></i>
                          <span>Image unavailable</span>
                        </div>
                        <div v-if="msg.content" class="media-caption">{{ msg.content }}</div>
                      </div>

                      <!-- Video -->
                      <div v-else-if="msg.message_type === 'video'" class="media-message">
                        <video
                          v-if="!videoErrors[msg.file_url]"
                          :src="msg.file_url"
                          class="msg-video"
                          controls
                          playsinline
                          preload="metadata"
                          @error="onVideoError(msg.file_url)"
                        ></video>
                        <div v-else class="media-error">
                          <i class="fas fa-video"></i>
                          <span>Video unavailable</span>
                        </div>
                      </div>

                      <!-- File -->
                      <div v-else-if="msg.message_type === 'file'" class="file-message" @click="openFile(msg.file_url)">
                        <i :class="getFileIcon(msg.file_name)"></i>
                        <div class="file-message-info">
                          <span class="file-message-name">{{ msg.file_name || 'File' }}</span>
                          <span class="file-message-size">{{ formatFileSize(msg.file_size) }}</span>
                        </div>
                        <i class="fas fa-download file-message-download"></i>
                      </div>

                      <!-- Products attached by the manager -->
                      <div v-if="msg.products && msg.products.length" class="msg-products">
                        <div v-if="msg.products.length > 1" class="msg-products-head">
                          <i class="fas fa-box-open"></i>
                          <span>{{ msg.products.length }} products from the marketplace</span>
                        </div>
                        <div
                          v-for="prod in msg.products"
                          :key="'prod-' + (prod.id || prod.name)"
                          class="msg-product-share"
                          @click="goToRecommended(msg, prod)"
                        >
                          <div class="mps-media">
                            <img v-if="prod.image" :src="prod.image" :alt="prod.name" loading="lazy" />
                            <span v-else class="mps-media-ph">{{ (prod.category || prod.name || 'P').charAt(0) }}</span>
                          </div>
                          <div class="mps-info">
                            <div class="mps-name">{{ truncateProduct(prod.name, 46) }}</div>
                            <div class="mps-price">{{ formatProductPrice(prod.price) }}</div>
                            <div class="mps-foot">
                              <span v-if="prod.category" class="mps-cat"><i class="fas fa-tag"></i> {{ prod.category }}</span>
                              <span class="mps-cta">See product <i class="fas fa-chevron-right"></i></span>
                            </div>
                          </div>
                        </div>
                        <button class="msg-all-products-btn" @click="goToRecommended(msg, null)">
                          <i class="fas fa-th-large"></i> View all products
                        </button>
                      </div>

                      <!-- Meta -->
                      <div class="msg-bubble-meta">
                        <span>{{ formatTime(msg.timestamp) }}</span>
                        <i v-if="msg.is_mine" class="fas" :class="msg.read ? 'fa-check-double read' : 'fa-check'"></i>
                      </div>

                      <!-- Reaction popup (shown briefly on double tap) -->
                      <transition name="pop">
                        <div v-if="msg.showReaction" class="message-reaction-popup">
                          <button v-for="r in quickReactions" :key="r" @click.stop="addReaction(msg, r)">{{ r }}</button>
                        </div>
                      </transition>
                    </div>
                  </div>
                </div>

                <!-- Typing indicator -->
                <div v-if="isTyping" class="message-row received">
                  <div class="msg-avatar-small" :class="{ sponsor: activeChannel === 'sponsor' }">
                    <i :class="activeChannel === 'support' ? 'fas fa-headset' : 'fas fa-user-tie'"></i>
                  </div>
                  <div class="msg-bubble typing-bubble">
                    <div class="typing-indicator">
                      <div class="typing-dot"></div>
                      <div class="typing-dot"></div>
                      <div class="typing-dot"></div>
                    </div>
                  </div>
                </div>
              </template>

              <!-- Scroll to bottom button -->
              <transition name="bounce">
                <div v-if="!isAtBottom && activeMessages.length > 0" class="scroll-to-bottom" @click="scrollToBottom(true)">
                  <i class="fas fa-arrow-down"></i>
                  <span v-if="activeUnreadCount > 0" class="scroll-badge">{{ activeUnreadCount }}</span>
                </div>
              </transition>
            </div>

            <!-- File preview -->
            <div v-if="selectedFile" class="file-preview-bar">
              <div class="file-preview-content">
                <img v-if="filePreview.type === 'image'" :src="filePreview.url" class="file-preview-thumb" />
                <video v-else-if="filePreview.type === 'video'" :src="filePreview.url" class="file-preview-thumb"></video>
                <div v-else class="file-preview-icon">
                  <i :class="getFileIcon(selectedFile.name)"></i>
                </div>
                <div class="file-preview-info">
                  <span class="file-preview-name">{{ selectedFile.name }}</span>
                  <span class="file-preview-size">{{ formatFileSize(selectedFile.size) }}</span>
                </div>
              </div>
              <button class="file-preview-remove" @click="removeFile">
                <i class="fas fa-times"></i>
              </button>
            </div>

            <!-- Input Area -->
            <div class="chat-input-area" :class="{ mobile: isMobile, 'keyboard-open': keyboardHeight > 0 }" :style="mobileInputStyle">
              <button class="input-action-btn attach-btn" @click="toggleAttachmentMenu">
                <i class="fas fa-plus" :class="{ rotate: showAttachmentMenu }"></i>
              </button>

              <textarea
                ref="messageInput"
                v-model="inputMessage"
                class="chat-input"
                :placeholder="activeChannel === 'support' ? 'Type your message...' : `Message ${sponsorName}...`"
                @input="autoResizeTextarea"
                :disabled="isSending"
                rows="1"
              ></textarea>

              <button class="input-action-btn emoji-btn" @click="toggleEmojiPicker">
                <i class="fas fa-smile"></i>
              </button>

              <button
                class="send-btn-inline"
                :class="{ 'sponsor-send': activeChannel === 'sponsor', 'has-text': inputMessage.trim().length > 0 || selectedFile }"
                @click="sendUserMessage"
                :disabled="isSending || (!inputMessage.trim() && !selectedFile)"
                :title="isSending ? 'Sending...' : 'Send message'"
              >
                <i v-if="!isSending" class="fas fa-paper-plane"></i>
                <div v-else class="send-spinner"></div>
              </button>
            </div>

            <!-- Attachment Menu -->
            <transition name="slide-up">
              <div v-if="showAttachmentMenu" class="attachment-panel">
                <div class="attachment-grid">
                  <button class="attachment-item" @click="selectFile('image')">
                    <div class="attachment-icon image-icon"><i class="fas fa-image"></i></div>
                    <span>Photo</span>
                  </button>
                  <button class="attachment-item" @click="selectFile('video')">
                    <div class="attachment-icon video-icon"><i class="fas fa-video"></i></div>
                    <span>Video</span>
                  </button>
                  <button class="attachment-item" @click="selectFile('file')">
                    <div class="attachment-icon file-icon"><i class="fas fa-file-alt"></i></div>
                    <span>Document</span>
                  </button>
                  <button class="attachment-item" @click="toggleEmojiPicker">
                    <div class="attachment-icon emoji-icon"><i class="fas fa-smile"></i></div>
                    <span>Emoji</span>
                  </button>
                </div>
              </div>
            </transition>

            <!-- Emoji picker -->
            <transition name="slide-up">
              <div v-if="showEmojiPicker" class="emoji-picker-panel">
                <div class="emoji-grid">
                  <button v-for="emoji in emojis" :key="emoji" class="emoji-btn" @click="addEmoji(emoji)">
                    {{ emoji }}
                  </button>
                </div>
              </div>
            </transition>
          </div><!-- fin .chat-main -->

          <!-- ═══════ BARRE DE NAVIGATION MOBILE (remplace la sidebar) ═══════ -->
          <nav v-if="isMobile" class="mobile-bottom-nav">
            <button
              class="mobile-nav-btn"
              :class="{ active: activeChannel === 'support' }"
              @click="switchChannel('support')"
            >
              <span v-if="activeChannel === 'support'" class="mobile-nav-indicator"></span>
              <i class="fas fa-headset"></i>
              <span class="mobile-nav-label">Support</span>
              <span v-if="supportUnreadCount > 0" class="mobile-nav-badge">
                {{ supportUnreadCount > 9 ? '9+' : supportUnreadCount }}
              </span>
            </button>

            <button
              class="mobile-nav-btn"
              :class="{ active: activeChannel === 'sponsor', disabled: !hasSponsor }"
              @click="hasSponsor && switchChannel('sponsor')"
            >
              <span v-if="activeChannel === 'sponsor'" class="mobile-nav-indicator"></span>
              <i class="fas fa-user-tie"></i>
              <span class="mobile-nav-label">Sponsor</span>
              <span v-if="hasSponsor" class="mobile-nav-status" :class="sponsorStatus"></span>
              <span v-if="sponsorUnreadCount > 0" class="mobile-nav-badge">
                {{ sponsorUnreadCount > 9 ? '9+' : sponsorUnreadCount }}
              </span>
            </button>
          </nav>

        </div>
      </div>
    </transition>

    <!-- Hidden file inputs -->
    <input ref="imageInput" type="file" accept="image/*" style="display: none" @change="handleFileSelect" />
    <input ref="videoInput" type="file" accept="video/*" style="display: none" @change="handleFileSelect" />
    <input ref="fileInput" type="file" accept="*/*" style="display: none" @change="handleFileSelect" />

    <!-- Image Modal -->
    <div v-if="showImageModal" class="custom-image-modal" @click.self="closeImageModal">
      <div class="custom-image-modal-content">
        <button class="custom-close-btn" @click="closeImageModal">
          <i class="fas fa-times"></i>
        </button>
        <img :src="selectedImageUrl" alt="Full size" />
      </div>
    </div>
  </div>
</template>
<script>
import axios from 'axios'

export default {
  name: 'MultiChannelChat',
  data() {
    return {
      isOpen: false,
      isFullscreen: false,
      activeChannel: null,
      showInvitationBubble: false,
      showChannelInfoPopup: false,
      channelInfoPopupDismissed: false,

      showNewMessageNotification: false,
      newMessageCount: 0,
      lastMessageCount: 0,
      isMobile: false,
      keyboardHeight: 0,
      isLandscape: false,
      inputMessage: '',
      selectedFile: null,
      filePreview: { type: null, url: null },
      fileCaption: '',
      isSending: false,
      isTyping: false,
      isAtBottom: true,
      isAtTop: false,
      showAttachmentMenu: false,
      showEmojiPicker: false,
      showImageModal: false,
      selectedImageUrl: '',
      isDraggingFile: false,
      emojis: ['😊', '😂', '❤️', '👍', '🙏', '🎉', '💪', '✨', '🔥', '👏', '💯', '✅', '🚀', '⭐', '💡', '😍', '🤔', '👌', '🎊', '💬'],
      quickReactions: ['❤️', '👍', '🔥', '👏', '😂', '😮'],
      supportMessages: [],
      supportUnreadCount: 0,
      supportLastFetch: 0,
      supportHistoryLoaded: false,
      isLoadingHistory: false,
      managerUsername: '',
      supportPolling: null,
      sponsorMessages: [],
      sponsorUnreadCount: 0,
      sponsorLastFetch: 0,
      sponsorHistoryLoaded: false,
      sponsorName: '',
      sponsorStatus: 'offline',
      hasSponsor: false,
      sponsorPolling: null,
      imageErrors: {},
      videoErrors: {},
      historyFetchInterval: 5000,
      quickSuggestions: [
        { text: 'My Orders', icon: 'fas fa-shopping-cart' },
        { text: 'Account Balance', icon: 'fas fa-wallet' },
        { text: 'Help Center', icon: 'fas fa-question-circle' },
        { text: 'Contact Support', icon: 'fas fa-headset' }
      ],
      bubbleTouchStartX: 0,
      bubbleTouchStartY: 0,
      bubbleShowTimeout: null,
      bubbleHideTimeout: null,
      notificationTimeout: null,
    }
  },

  computed: {
    shouldShowButton() {
      const hideRoutes = ['dashboard.chat', 'chat_history']
      return !hideRoutes.includes(this.$route?.name)
    },
    totalUnreadCount() {
      return this.supportUnreadCount + this.sponsorUnreadCount
    },
    activeMessages() {
      return this.activeChannel === 'support' ? this.supportMessages : this.sponsorMessages
    },
    activeUnreadCount() {
      return this.activeChannel === 'support' ? this.supportUnreadCount : this.sponsorUnreadCount
    },
    mobileInputStyle() {
      if (this.isMobile && this.keyboardHeight > 0) {
        return { paddingBottom: '8px' }
      }
      return {}
    },
    overlayStyle() {
      if (!this.isMobile || this.keyboardHeight <= 0) return {}
      return {
        height: `${window.innerHeight - this.keyboardHeight}px`,
        top: 0,
        bottom: 'auto',
        overflow: 'hidden'
      }
    }
  },

  watch: {
    activeChannel(newVal) {
      if (newVal) {
        this.newMessageCount = 0
        this.showNewMessageNotification = false
        this.loadChannelHistory(newVal, true)
        this.$nextTick(() => {
          setTimeout(() => {
            this.lastMessageCount = this.activeMessages.length
            this.scrollToBottom(true)
            this.focusInput()
          }, 200)
        })
      }
    },
  isOpen(newVal) {
    if (this.isMobile) {
      document.body.style.overflow = newVal ? 'hidden' : ''
      document.documentElement.style.overflow = newVal ? 'hidden' : ''
    }
    if (newVal) {
      this.$nextTick(() => this.refreshTranslation())
    }
  },
    inputMessage() {
      this.$nextTick(() => this.autoResizeTextarea())
    },
    supportMessages: {
      handler(newVal) {
        if (!this.isOpen) return
        const newCount = newVal.length
        const countDiff = newCount - this.lastMessageCount
        if (countDiff > 0) {
          if (this.activeChannel === 'support') {
            this.$nextTick(() => {
              setTimeout(() => {
                this.scrollToBottom(true)
                this.detectAndMarkAsRead()
              }, 50)
            })
          } else {
            this.showNewMessageNotification = true
            this.newMessageCount += countDiff
            this.resetNotificationTimeout()
          }
        }
        this.lastMessageCount = newCount
      },
      deep: true
    },
    sponsorMessages: {
      handler(newVal) {
        if (!this.isOpen) return
        const newCount = newVal.length
        const countDiff = newCount - this.lastMessageCount
        if (countDiff > 0) {
          if (this.activeChannel === 'sponsor') {
            this.$nextTick(() => {
              setTimeout(() => {
                this.scrollToBottom(true)
                this.detectAndMarkAsRead()
              }, 50)
            })
          } else {
            this.showNewMessageNotification = true
            this.newMessageCount += countDiff
            this.resetNotificationTimeout()
          }
        }
        this.lastMessageCount = newCount
      },
      deep: true
    }
  },

  methods: {
    checkMobile() {
      const width = window.innerWidth
      this.isMobile = width <= 768
      this.isLandscape = window.innerHeight < window.innerWidth && width <= 768
    },
    handleKeyboard() {
      if (window.visualViewport) {
        const heightDiff = window.innerHeight - window.visualViewport.height
        this.keyboardHeight = heightDiff > 100 ? heightDiff : 0
        if (this.keyboardHeight > 0 && this.isOpen) {
          this.$nextTick(() => this.scrollToBottom(true))
        }
      }
    },
    focusInput() {
      this.$nextTick(() => {
        if (!this.isMobile && this.$refs.messageInput) {
          this.$refs.messageInput.focus()
        }
      })
    },
    autoResizeTextarea() {
      const el = this.$refs.messageInput
      if (!el) return
      el.style.height = 'auto'
      const newHeight = Math.min(el.scrollHeight, 100)
      el.style.height = newHeight + 'px'
    },
    handleEnterKey(e) {
      if (this.isMobile) {
        this.sendUserMessage()
      } else {
        if (!e.shiftKey) {
          this.sendUserMessage()
        } else {
          this.inputMessage += '\n'
        }
      }
    },
    openChat() {
      if (!this.channelInfoPopupDismissed) {
        this.showChannelInfoPopup = true
        return
      }
      this.isOpen = true
      this.activeChannel = 'support'
      this.newMessageCount = 0
      this.showNewMessageNotification = false
      this.loadChannelHistory('support', true)
      this.$nextTick(() => {
        setTimeout(() => {
          this.scrollToBottom(true)
          this.detectAndMarkAsRead()
          this.focusInput()
        }, 150)
      })
    },
    closeChannelInfoPopup(openChat = true) {
      this.showChannelInfoPopup = false
      localStorage.setItem('pettycash_chat_info_dismissed', 'true')
      this.channelInfoPopupDismissed = true
      if (openChat) {
        this.isOpen = true
        this.activeChannel = 'support'
        this.newMessageCount = 0
        this.showNewMessageNotification = false
        this.loadChannelHistory('support', true)
        this.$nextTick(() => {
          setTimeout(() => {
            this.scrollToBottom(true)
            this.detectAndMarkAsRead()
            this.focusInput()
          }, 150)
        })
      }
    },
    backToMenu() {
      this.activeChannel = null
      this.closeChat()
    },
    switchChannel(channel) {
      if (channel === this.activeChannel) return
      this.activeChannel = channel
      this.newMessageCount = 0
      this.showNewMessageNotification = false
      this.showEmojiPicker = false
      this.showAttachmentMenu = false
      this.$nextTick(() => {
        setTimeout(() => {
          this.detectAndMarkAsRead()
        }, 100)
      })
    },
    closeChat() {
      this.isOpen = false
      this.activeChannel = null
      this.showInvitationBubble = false
      this.showEmojiPicker = false
      this.showAttachmentMenu = false
      this.inputMessage = ''
      this.selectedFile = null
      this.newMessageCount = 0
      this.showNewMessageNotification = false
    },
    toggleFullscreen() {
      this.isFullscreen = !this.isFullscreen
      this.$nextTick(() => this.scrollToBottom())
    },
    dismissBubble() {
      this.showInvitationBubble = false
      this.bubblePermanentlyDismissed = true

      if (this.bubbleShowTimeout) clearTimeout(this.bubbleShowTimeout)
      if (this.bubbleHideTimeout) clearTimeout(this.bubbleHideTimeout)
    },
    handleBubbleTouchStart(e) {
      this.bubbleTouchStartX = e.touches[0].clientX
      this.bubbleTouchStartY = e.touches[0].clientY
    },
    handleBubbleTouchEnd(e) {
      const diffX = e.changedTouches[0].clientX - this.bubbleTouchStartX
      const diffY = e.changedTouches[0].clientY - this.bubbleTouchStartY
      if (Math.abs(diffX) > 80 && Math.abs(diffY) < 50) {
        this.dismissBubble()
      }
    },
    resetNotificationTimeout() {
      if (this.notificationTimeout) clearTimeout(this.notificationTimeout)
      this.notificationTimeout = setTimeout(() => {
        this.showNewMessageNotification = false
      }, 5000)
    },
    sendUserMessage() {
      if ((!this.inputMessage.trim() && !this.selectedFile) || this.isSending) return
      const message = this.inputMessage.trim()
      const file = this.selectedFile
      this.resetForm()
      if (this.activeChannel === 'support') {
        this.sendSupportMessage(message, file)
      } else {
        this.sendSponsorMessage(message, file)
      }
    },
    async sendMessage(text) {
      this.inputMessage = text
      await this.sendUserMessage()
    },
    async sendSupportMessage(message, file = null) {
      const tempId = Date.now()
      const tempMsg = {
        id: tempId,
        content: message || (file ? '📎 Sending file...' : ''),
        timestamp: new Date().toISOString(),
        sender: this.managerUsername || 'Me',
        is_mine: true,
        message_type: file ? this.getFileType(file) : 'text',
        file_url: file ? URL.createObjectURL(file) : null,
        file_name: file ? file.name : null,
        file_size: file ? file.size : null,
        is_temp: true,
      }
      this.supportMessages.push(tempMsg)
      this.$nextTick(() => {
        this.refreshTranslation()
        setTimeout(() => this.scrollToBottom(true), 50)
      })
      this.isSending = true
      try {
        const formData = new FormData()
        formData.append('message', message)
        formData.append('message_type', file ? this.getFileType(file) : 'text')
        if (file) formData.append('file', file)
        await axios.post('/api/v1/home/save_chat/', formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        })
        const idx = this.supportMessages.findIndex(m => m.id === tempId)
        if (idx !== -1) this.supportMessages.splice(idx, 1)
        setTimeout(() => this.loadChannelHistory('support', true), 300)
      } catch (error) {
        const idx = this.supportMessages.findIndex(m => m.id === tempId)
        if (idx !== -1) this.supportMessages.splice(idx, 1)
        console.error('Error sending support message:', error)
        this.showSnackBar('Failed to send message. Please try again.', 'red darken-4')
      } finally {
        this.isSending = false
      }
    },
    async loadSupportHistory(shouldAutoScroll = true, isBackground = false) {
      const now = Date.now()
      if (now - this.supportLastFetch < this.historyFetchInterval && !isBackground) return
      this.supportLastFetch = now
      if (!isBackground) this.isLoadingHistory = true
      try {
        const response = await axios.post('/api/v1/home/get_chat_history/')
        if (response.data && response.data.list_message) {
          const oldCount = this.supportMessages.length
          this.processSupportMessages(response.data.list_message)
          this.managerUsername = response.data.priority || ''
          const hasNew = this.supportMessages.length > oldCount
          if (hasNew && !isBackground) this.notifyNewMessage('support')
          if (!isBackground && this.activeChannel === 'support' && this.isOpen) {
            this.$nextTick(() => {
              setTimeout(() => this.scrollToBottom(true), 50)
            })
          }
          this.supportHistoryLoaded = true
        }
      } catch (error) {
        console.error('Error loading support history:', error)
      } finally {
        if (!isBackground) this.isLoadingHistory = false
      }
    },
    processSupportMessages(newMessages) {
      const processed = []
      const tempMap = new Map()
      newMessages.forEach(msg => {
        if (!msg.message_type && msg.type) msg.message_type = msg.type
        const uniqueKey = this.createMessageKey(msg)
        const dedupeKey = this.createDeduplicationKey(msg)
        const isDuplicate = processed.some(m => this.createDeduplicationKey(m) === dedupeKey)
        if (!tempMap.has(uniqueKey) && !isDuplicate) {
          tempMap.set(uniqueKey, msg)
          processed.push({ ...msg, isNew: false })
        }
      })
      processed.sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp))
      this.supportMessages = processed
      this.$nextTick(() => this.refreshTranslation())
    },
    async fetchSupportUnread() {
      try {
        const res = await axios.post('/api/v1/home/check_chat/')
        this.supportUnreadCount = res.data.unread || 0
      } catch (e) {
        console.error('Error fetching support unread:', e)
      }
    },
    async sendSponsorMessage(message, file = null) {
      const tempId = Date.now()
      const tempMsg = {
        id: tempId,
        content: message || (file ? '📎 Sending file...' : ''),
        timestamp: new Date().toISOString(),
        sender: this.sponsorName || 'Sponsor',
        is_mine: true,
        message_type: file ? this.getFileType(file) : 'text',
        file_url: file ? URL.createObjectURL(file) : null,
        file_name: file ? file.name : null,
        file_size: file ? file.size : null,
        is_temp: true,
      }
      this.sponsorMessages.push(tempMsg)
      this.$nextTick(() => {
        this.refreshTranslation()
        setTimeout(() => this.scrollToBottom(true), 50)
      })
      this.isSending = true
      try {
        const formData = new FormData()
        formData.append('message', message)
        formData.append('message_type', file ? this.getFileType(file) : 'text')
        if (file) formData.append('file', file)
        await axios.post('/api/v1/home/sponsor/message/', formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        })
        const idx = this.sponsorMessages.findIndex(m => m.id === tempId)
        if (idx !== -1) this.sponsorMessages.splice(idx, 1)
        setTimeout(() => this.loadSponsorHistory(true, true, false), 300)
      } catch (error) {
        const idx = this.sponsorMessages.findIndex(m => m.id === tempId)
        if (idx !== -1) this.sponsorMessages.splice(idx, 1)
        console.error('Error sending sponsor message:', error)
        this.showSnackBar('Failed to send message to sponsor.', 'red darken-4')
      } finally {
        this.isSending = false
      }
    },
    async loadSponsorHistory(shouldAutoScroll = true, markAsRead = false, isBackground = false) {
      const now = Date.now()
      if (now - this.sponsorLastFetch < this.historyFetchInterval && !isBackground) return
      this.sponsorLastFetch = now
      if (!isBackground) this.isLoadingHistory = true
      try {
        const markParam = markAsRead ? 'true' : 'false'
        const response = await axios.get(`/api/v1/home/sponsor/conversation/?mark_read=${markParam}`)
        if (response.data) {
          this.hasSponsor = !!response.data.conversation_id
          this.sponsorName = response.data.sponsor_name || ''
          this.sponsorStatus = 'online'
          if (response.data.messages) {
            const oldCount = this.sponsorMessages.length
            this.processSponsorMessages(response.data.messages)
            const hasNew = this.sponsorMessages.length > oldCount
            if (hasNew && !isBackground) this.notifyNewMessage('sponsor')
            if (!isBackground && this.activeChannel === 'sponsor' && this.isOpen) {
              this.$nextTick(() => {
                setTimeout(() => this.scrollToBottom(true), 50)
              })
            }
          } else {
            this.sponsorMessages = []
          }
          this.sponsorHistoryLoaded = true
        }
      } catch (error) {
        console.error('Error loading sponsor history:', error)
        if (error.response && error.response.status === 400) {
          this.hasSponsor = false
          this.sponsorName = ''
          this.sponsorMessages = []
        }
      } finally {
        if (!isBackground) this.isLoadingHistory = false
      }
    },
    processSponsorMessages(messages) {
      const processed = []
      const tempMap = new Map()
      messages.forEach(msg => {
        const uniqueKey = this.createMessageKey(msg)
        const dedupeKey = this.createDeduplicationKey(msg)
        const isDuplicate = processed.some(m => this.createDeduplicationKey(m) === dedupeKey)
        if (!tempMap.has(uniqueKey) && !isDuplicate) {
          tempMap.set(uniqueKey, msg)
          processed.push({ ...msg, isNew: false })
        }
      })
      processed.sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp))
      this.sponsorMessages = processed
      this.$nextTick(() => this.refreshTranslation())
    },
    async fetchSponsorUnread() {
      try {
        const res = await axios.get('/api/v1/home/sponsor/unread_count/')
        this.sponsorUnreadCount = res.data.unread || 0
      } catch (e) {
        console.error('Error fetching sponsor unread:', e)
      }
    },
    async markSupportRead() {
      if (this.supportMessages.length === 0) return
      try {
        this.supportMessages.forEach(msg => {
          if (!msg.is_mine && !msg.read) {
            this.$set(msg, 'read', true)
          }
        })
        await axios.post('/api/v1/home/check_chat/')
      } catch (e) {
        console.error('Mark support read error:', e)
      }
    },
    async markSponsorReadMessages() {
      if (this.sponsorMessages.length === 0) return
      try {
        this.sponsorMessages.forEach(msg => {
          if (!msg.is_mine && !msg.read) {
            this.$set(msg, 'read', true)
          }
        })
        await axios.post('/api/v1/home/sponsor/mark_read/')
      } catch (e) {
        console.error('Mark sponsor read error:', e)
      }
    },
    detectAndMarkAsRead() {
      if (!this.isOpen) return
      if (this.activeChannel === 'support') {
        this.markSupportRead()
      } else if (this.activeChannel === 'sponsor') {
        this.markSponsorReadMessages()
      }
    },
    async checkSponsorPresence() {
      try {
        const response = await axios.get('/api/v1/home/sponsor/conversation/?mark_read=false')
        if (response.data && response.data.conversation_id) {
          this.hasSponsor = true
          this.sponsorName = response.data.sponsor_name || 'Sponsor'
          this.sponsorStatus = 'online'
          if (!this.sponsorHistoryLoaded && this.activeChannel !== 'sponsor') {
            this.sponsorMessages = response.data.messages || []
            this.sponsorHistoryLoaded = true
            this.$nextTick(() => this.refreshTranslation())
          }
          await this.fetchSponsorUnread()
        } else {
          this.hasSponsor = false
          this.sponsorName = ''
        }
      } catch (error) {
        this.hasSponsor = false
        this.sponsorName = ''
      }
    },
    loadChannelHistory(channel, shouldAutoScroll = true) {
      if (channel === 'support') {
        this.loadSupportHistory(shouldAutoScroll, false)
      } else {
        this.loadSponsorHistory(shouldAutoScroll, true, false)
        const markRead = shouldAutoScroll && !this.sponsorHistoryLoaded
        if (markRead) this.markSponsorRead()
        this.loadSponsorHistory(shouldAutoScroll, markRead, false)
      }
      this.refreshTranslationDebounced()
    },
    refreshTranslationDebounced() {
      clearTimeout(this.__translationRefreshTimer)
      this.__translationRefreshTimer = setTimeout(() => {
        this.refreshTranslation()
      }, 120)
    },
    refreshTranslation() {
      if (this.$translator && typeof this.$translator.refreshIfEnabled === 'function') {
        this.$translator.refreshIfEnabled('dom', this.$refs.messagesContainer || this.$el)
      }
    },
    createDeduplicationKey(msg) {
      const timestamp = msg.timestamp ? new Date(msg.timestamp).getTime() : Date.now()
      const normalizedTimestamp = Math.floor(timestamp / 1000) * 1000
      return `dedupe-${normalizedTimestamp}-${msg.sender || 'unknown'}-${msg.message_type || 'text'}-${(msg.content || '').trim()}-${msg.file_url || ''}`
    },
    createMessageKey(msg) {
      if (msg.id) return `id-${msg.id}`
      const timestamp = msg.timestamp ? new Date(msg.timestamp).getTime() : Date.now()
      return `fallback-${timestamp}-${msg.sender || 'unknown'}-${msg.message_type || 'text'}-${(msg.content || '').substring(0, 10)}-${(msg.file_url || '').substring(0, 20)}`
    },
    notifyNewMessage(channel) {
      if (this.isMobile && navigator.vibrate) {
        navigator.vibrate(50)
      }
      if (this.activeChannel !== channel || !this.isOpen) {
        this.playNotificationSound()
      }
    },
    playNotificationSound() {
      try {
        const AudioContext = window.AudioContext || window.webkitAudioContext
        if (!AudioContext) return
        const ctx = new AudioContext()
        const osc = ctx.createOscillator()
        const gain = ctx.createGain()
        osc.connect(gain)
        gain.connect(ctx.destination)
        osc.frequency.value = 800
        osc.type = 'sine'
        gain.gain.setValueAtTime(0.1, ctx.currentTime)
        gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.3)
        osc.start(ctx.currentTime)
        osc.stop(ctx.currentTime + 0.3)
      } catch (e) {
        // Silent fail
      }
    },
    toggleAttachmentMenu() {
      this.showAttachmentMenu = !this.showAttachmentMenu
      this.showEmojiPicker = false
    },
    selectFile(type) {
      this.showAttachmentMenu = false
      if (type === 'image') this.$refs.imageInput.click()
      else if (type === 'video') this.$refs.videoInput.click()
      else this.$refs.fileInput.click()
    },
    handleFileSelect(event) {
      const file = event.target.files[0]
      if (!file) return
      this.processFile(file)
      event.target.value = ''
    },
    onDragOver(e) {
      if (!e.dataTransfer.types.includes('Files')) return
      this.isDraggingFile = true
    },
    onDragLeave(e) {
      this.isDraggingFile = false
    },
    onDrop(e) {
      this.isDraggingFile = false
      const files = e.dataTransfer.files
      if (files.length > 0) {
        this.processFile(files[0])
      }
    },
    processFile(file) {
      const maxSize = file.type.startsWith('video/') ? 2000 * 1024 * 1024 : 10 * 1024 * 1024
      if (file.size > maxSize) {
        const maxMB = maxSize / (1024 * 1024)
        this.showSnackBar(`File size must be less than ${maxMB}MB`, 'red darken-4')
        return
      }
      this.selectedFile = file
      this.createFilePreview(file)
    },
    createFilePreview(file) {
      const reader = new FileReader()
      reader.onload = (e) => {
        this.filePreview.url = e.target.result
        if (file.type.startsWith('image/')) this.filePreview.type = 'image'
        else if (file.type.startsWith('video/')) this.filePreview.type = 'video'
        else this.filePreview.type = 'file'
      }
      reader.readAsDataURL(file)
    },
    removeFile() {
      this.selectedFile = null
      this.fileCaption = ''
      this.filePreview = { type: null, url: null }
    },
    getFileType(file) {
      if (file.type.startsWith('image/')) return 'image'
      if (file.type.startsWith('video/')) return 'video'
      return 'file'
    },
    getFileIcon(fileName) {
      if (!fileName) return 'fas fa-file'
      const ext = fileName.split('.').pop().toLowerCase()
      const iconMap = {
        pdf: 'fas fa-file-pdf', doc: 'fas fa-file-word', docx: 'fas fa-file-word',
        xls: 'fas fa-file-excel', xlsx: 'fas fa-file-excel', ppt: 'fas fa-file-powerpoint',
        pptx: 'fas fa-file-powerpoint', txt: 'fas fa-file-alt', zip: 'fas fa-file-archive',
        rar: 'fas fa-file-archive', jpg: 'fas fa-file-image', jpeg: 'fas fa-file-image',
        png: 'fas fa-file-image', gif: 'fas fa-file-image', mp4: 'fas fa-file-video',
        mp3: 'fas fa-file-audio'
      }
      return iconMap[ext] || 'fas fa-file'
    },
    formatFileSize(bytes) {
      if (!bytes) return '0 B'
      const sizes = ['B', 'KB', 'MB', 'GB']
      const i = Math.floor(Math.log(bytes) / Math.log(1024))
      return Math.round(bytes / Math.pow(1024, i) * 100) / 100 + ' ' + sizes[i]
    },
    formatTime(timestamp) {
      if (!timestamp) return ''
      const date = new Date(timestamp)
      const now = new Date()
      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate())
      const msgDate = new Date(date.getFullYear(), date.getMonth(), date.getDate())
      if (msgDate.getTime() === today.getTime()) {
        return date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })
      }
      return date.toLocaleDateString('en-US', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })
    },
    showDateSeparator(msg, index) {
      if (index === 0) return true
      const prev = this.activeMessages[index - 1]
      if (!prev || !prev.timestamp || !msg.timestamp) return false
      const prevDate = new Date(prev.timestamp).toDateString()
      const currDate = new Date(msg.timestamp).toDateString()
      return prevDate !== currDate
    },
    formatDateSeparator(timestamp) {
      if (!timestamp) return ''
      const date = new Date(timestamp)
      const now = new Date()
      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate())
      const yesterday = new Date(today)
      yesterday.setDate(yesterday.getDate() - 1)
      if (date.toDateString() === today.toDateString()) return 'Today'
      if (date.toDateString() === yesterday.toDateString()) return 'Yesterday'
      return date.toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' })
    },
    formatMessage(text) {
      if (!text) return ''
      const urlRegex = /(https?:\/\/[^\s]+)/g
      text = text.replace(urlRegex, '<a href="$1" target="_blank" class="message-link">$1</a>')
      text = text.replace(/\n/g, '<br>')
      return text
    },

    goToRecommended(msg, prod) {
      const route = prod && prod.id
        ? `/market_place/recommended/${msg.id}?highlight=${prod.id}`
        : `/market_place/recommended/${msg.id}`
      this.closeChat()
      if (this.$router) this.$router.push(route)
    },

    truncateProduct(text, max) {
      if (!text) return ''
      return text.length > max ? text.substring(0, max) + '…' : text
    },

    formatProductPrice(value) {
      if (value === null || value === undefined || value === '') return ''
      const num = Number(value)
      if (isNaN(num)) return String(value)
      return num.toLocaleString('fr-FR') + ' FCFA'
    },
    scrollToBottom(force = false) {
      const container = this.$refs.messagesContainer
      if (container) {
        container.scrollTop = container.scrollHeight
        if (force) this.isAtBottom = true
      }
    },
    scrollToTop() {
      const container = this.$refs.messagesContainer
      if (container) {
        container.scrollTo({ top: 0, behavior: 'smooth' })
      }
    },
    handleScroll() {
      const container = this.$refs.messagesContainer
      if (container) {
        const threshold = 120
        this.isAtBottom = container.scrollTop + container.clientHeight >= container.scrollHeight - threshold
        this.isAtTop = container.scrollTop <= threshold
      }
    },
    handleImageError(url) {
      this.$set(this.imageErrors, url, true)
    },
    onVideoError(url) {
      this.$set(this.videoErrors, url, true)
    },
    openImageModal(url) {
      if (!this.imageErrors[url]) {
        this.selectedImageUrl = url
        this.showImageModal = true
      }
    },
    closeImageModal() {
      this.showImageModal = false
      this.selectedImageUrl = ''
    },
    openFile(url) {
      window.open(url, '_blank')
    },
    toggleEmojiPicker() {
      this.showEmojiPicker = !this.showEmojiPicker
      this.showAttachmentMenu = false
    },
    addEmoji(emoji) {
      this.inputMessage += emoji
      this.showEmojiPicker = false
      this.$nextTick(() => {
        this.autoResizeTextarea()
        this.$refs.messageInput?.focus()
      })
    },
    handleMessageDoubleTap(msg) {
      this.$set(msg, 'showReaction', true)
      setTimeout(() => {
        this.$set(msg, 'showReaction', false)
      }, 3000)
    },
    addReaction(msg, reaction) {
      this.$set(msg, 'showReaction', false)
      this.showSnackBar(`Reacted with ${reaction}`, 'primary')
    },
    resetForm() {
      this.inputMessage = ''
      this.fileCaption = ''
      this.selectedFile = null
      this.filePreview = { type: null, url: null }
      this.showEmojiPicker = false
      this.showAttachmentMenu = false
      this.$nextTick(() => {
        if (this.$refs.messageInput) {
          this.$refs.messageInput.style.height = 'auto'
        }
      })
    },
    showSnackBar(message, color) {
      this.$store.commit('setSnackBarMessage', message)
      this.$store.commit('setSnackBarColor', color)
      this.$store.commit('activateSnackBar', true)
    },
  },

  mounted() {
    this.checkMobile()
    window.addEventListener('resize', this.checkMobile)
    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', this.handleKeyboard)
    }

    // La bulle d'invitation s'affiche toujours après 3 secondes (sans persistance)
    this.bubbleShowTimeout = setTimeout(() => {
      this.showInvitationBubble = true
      this.bubbleHideTimeout = setTimeout(() => {
        this.showInvitationBubble = false
      }, 10000)
    }, 3000)

    const infoDismissed = localStorage.getItem('pettycash_chat_info_dismissed')
    this.channelInfoPopupDismissed = infoDismissed === 'true'

    this.fetchSupportUnread()
    this.checkSponsorPresence()

    this.supportPolling = setInterval(() => {
      this.fetchSupportUnread()
      if (this.activeChannel === 'support' && !this.isLoadingHistory) {
        const now = Date.now()
        if (now - this.supportLastFetch >= this.historyFetchInterval) {
          this.loadSupportHistory(false, true)
        }
      }
    }, 8000)

    this.sponsorPolling = setInterval(() => {
      if (this.hasSponsor) {
        this.fetchSponsorUnread()
        if (this.activeChannel === 'sponsor' && !this.isLoadingHistory) {
          const now = Date.now()
          if (now - this.sponsorLastFetch >= this.historyFetchInterval) {
            this.loadSponsorHistory(false, false, true)
          }
        }
      } else {
        this.checkSponsorPresence()
      }
    }, 8000)

    this.$root.$on('open-chatbot', (data) => {
      this.isOpen = true
      this.activeChannel = 'support'
      this.newMessageCount = 0
      this.showNewMessageNotification = false
      this.loadSupportHistory(true, false)
      if (data && data.autoMessage) {
        this.$nextTick(() => {
          setTimeout(() => {
            this.inputMessage = data.autoMessage
            this.sendUserMessage()
          }, 500)
        })
      }
    })
  },

  beforeDestroy() {
    if (this.supportPolling) clearInterval(this.supportPolling)
    if (this.sponsorPolling) clearInterval(this.sponsorPolling)
    if (typeof window !== 'undefined') {
      window.removeEventListener('resize', this.handleResize)
      // Nettoyage au cas où
      document.body.style.overflow = ''
      document.documentElement.style.overflow = ''
    }
    if (this.bubbleShowTimeout) clearTimeout(this.bubbleShowTimeout)
    if (this.bubbleHideTimeout) clearTimeout(this.bubbleHideTimeout)
    if (this.notificationTimeout) clearTimeout(this.notificationTimeout)
    window.removeEventListener('resize', this.checkMobile)
    if (window.visualViewport) {
      window.visualViewport.removeEventListener('resize', this.handleKeyboard)
    }
    this.$root.$off('open-chatbot')
  }
}
</script>
<style scoped>
/* ══════════════════════════════════════════════════════
   TRANSITIONS
══════════════════════════════════════════════════════ */
.bubble-slide-enter-active, .bubble-slide-leave-active { transition: all 0.45s cubic-bezier(0.34, 1.56, 0.64, 1); }
.bubble-slide-enter, .bubble-slide-leave-to { opacity: 0; transform: translateX(40px) scale(0.85) rotate(3deg); }
.window-slide-enter-active, .window-slide-leave-active { transition: all 0.5s cubic-bezier(0.34, 1.56, 0.64, 1); }
.window-slide-enter, .window-slide-leave-to { opacity: 0; transform: translateY(50px) scale(0.92); }
.bounce-enter-active { animation: bounce-in 0.5s cubic-bezier(0.34, 1.56, 0.64, 1); }
.bounce-leave-active { animation: bounce-in 0.3s reverse; }
@keyframes bounce-in { 0% { transform: scale(0); opacity: 0; } 60% { transform: scale(1.12); } 100% { transform: scale(1); opacity: 1; } }
.slide-up-enter-active, .slide-up-leave-active { transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1); }
.slide-up-enter, .slide-up-leave-to { transform: translateY(20px); opacity: 0; }
.fade-enter-active, .fade-leave-active { transition: opacity 0.25s ease; }
.fade-enter, .fade-leave-to { opacity: 0; }
.pop-enter-active, .pop-leave-active { transition: all 0.2s cubic-bezier(0.34, 1.56, 0.64, 1); }
.pop-enter, .pop-leave-to { opacity: 0; transform: scale(0.5) translateY(10px); }

/* ══════════════════════════════════════════════════════
   CLOSED STATE — bouton flottant
══════════════════════════════════════════════════════ */
.chat-closed-state { position: fixed; bottom: 30px; right: 30px; z-index: 1000; display: flex; flex-direction: column; align-items: flex-end; gap: 14px; }
.chat-closed-state.mobile { bottom: 141px; right: 20px; padding-bottom: env(safe-area-inset-bottom); padding-right: env(safe-area-inset-right); }

.invitation-bubble { background: #ffffff; border-radius: 22px; padding: 18px 22px; max-width: 300px; box-shadow: 0 10px 40px rgba(0,0,0,0.08), 0 2px 8px rgba(0,0,0,0.04); position: relative; cursor: pointer; transition: all 0.35s cubic-bezier(0.34, 1.56, 0.64, 1); border: 1px solid #e5e7eb; transform-origin: bottom right; user-select: none; }
.invitation-bubble::after { content: ''; position: absolute; bottom: -10px; right: 32px; width: 20px; height: 20px; background: #ffffff; transform: rotate(45deg); box-shadow: 4px 4px 10px rgba(0,0,0,0.03); border-right: 1px solid #e5e7eb; border-bottom: 1px solid #e5e7eb; }
.invitation-bubble:hover { transform: translateY(-5px) scale(1.02); box-shadow: 0 16px 50px rgba(0,0,0,0.1), 0 4px 12px rgba(0,0,0,0.04); }
.bubble-close { position: absolute; top: 12px; right: 14px; width: 24px; height: 24px; border-radius: 50%; background: #f1f5f9; border: none; cursor: pointer; display: flex; align-items: center; justify-content: center; opacity: 0; transition: all 0.2s ease; color: #6b7280; font-size: 10px; }
.invitation-bubble:hover .bubble-close { opacity: 1; }
.bubble-close:hover { background: #e2e8f0; color: #374151; }
.bubble-header { display: flex; align-items: center; gap: 10px; margin-bottom: 10px; }
.bubble-avatar { width: 36px; height: 36px; border-radius: 50%; background: linear-gradient(135deg, #2563eb, #1d4ed8); display: flex; align-items: center; justify-content: center; flex-shrink: 0; box-shadow: 0 4px 12px rgba(37, 99, 235, 0.3); }
.bubble-avatar i { color: #ffffff; font-size: 14px; }
.bubble-title { font-size: 14px; font-weight: 700; color: #1f2937; }
.bubble-text { font-size: 13px; color: #4b5563; line-height: 1.6; margin-left: 46px; }
.bubble-text .highlight { color: #2563eb; font-weight: 600; }
.bubble-time { font-size: 11px; color: #9ca3af; margin-left: 46px; margin-top: 8px; display: flex; align-items: center; gap: 4px; }
.bubble-time i { font-size: 10px; color: #2563eb; }

.main-fab { width: 64px; height: 64px; border-radius: 50%; background: linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%); border: none; cursor: pointer; display: flex; align-items: center; justify-content: center; box-shadow: 0 8px 30px rgba(37, 99, 235, 0.25), 0 0 0 0 rgba(37, 99, 235, 0.15); color: #ffffff; font-size: 26px; transition: all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1); position: relative; animation: pulse-blue 2.5s infinite; }
.main-fab.has-unread { animation: pulse-blue 1.5s infinite; }
@keyframes pulse-blue { 0%, 100% { box-shadow: 0 8px 30px rgba(37, 99, 235, 0.25), 0 0 0 0 rgba(37, 99, 235, 0.15); } 50% { box-shadow: 0 8px 35px rgba(37, 99, 235, 0.35), 0 0 0 18px rgba(37, 99, 235, 0); } }
.main-fab:hover { transform: scale(1.12) rotate(-5deg); }
.main-fab:active { transform: scale(0.95); }
.fab-badge { position: absolute; top: -4px; right: -4px; background: linear-gradient(135deg, #ef4444, #dc2626); color: #ffffff; font-size: 11px; font-weight: 800; min-width: 24px; height: 24px; border-radius: 12px; display: flex; align-items: center; justify-content: center; border: 3px solid #ffffff; box-shadow: 0 3px 10px rgba(239, 68, 68, 0.4); animation: bounce-in 0.5s cubic-bezier(0.34, 1.56, 0.64, 1); z-index: 2; }
.fab-message-label { position: absolute; top: -40px; right: 0; background: #1f2937; color: #ffffff; padding: 8px 14px; border-radius: 12px; font-size: 12px; font-weight: 500; white-space: nowrap; box-shadow: 0 4px 15px rgba(0, 0, 0, 0.2); border: 1px solid #374151; animation: slide-down 0.3s ease; z-index: 1; }
@keyframes slide-down { from { opacity: 0; transform: translateY(-10px); } to { opacity: 1; transform: translateY(0); } }
.fab-pulse-ring { position: absolute; top: 0; left: 0; width: 100%; height: 100%; border-radius: 50%; border: 2px solid #ef4444; animation: ring-pulse 2s infinite; pointer-events: none; }
@keyframes ring-pulse { 0% { transform: scale(1); opacity: 0.6; } 100% { transform: scale(1.6); opacity: 0; } }

/* ══════════════════════════════════════════════════════
   POPUP
══════════════════════════════════════════════════════ */
.channel-info-popup-overlay { position: fixed; top: 0; left: 0; right: 0; bottom: 0; background: rgba(0,0,0,0.6); backdrop-filter: blur(4px); display: flex; align-items: center; justify-content: center; z-index: 2000; }
.channel-info-popup { background: #ffffff; border-radius: 28px; width: 90%; max-width: 400px; padding: 24px; box-shadow: 0 25px 50px -12px rgba(0,0,0,0.25); animation: slideUp 0.3s cubic-bezier(0.34, 1.56, 0.64, 1); }
@keyframes slideUp { from { opacity: 0; transform: translateY(20px); } to { opacity: 1; transform: translateY(0); } }
.popup-header { display: flex; align-items: center; gap: 12px; margin-bottom: 20px; }
.popup-header i { font-size: 32px; color: #2563eb; }
.popup-header h3 { margin: 0; font-size: 22px; font-weight: 700; color: #1f2937; }
.popup-body { margin-bottom: 24px; }
.popup-body p { font-size: 14px; color: #4b5563; margin: 0 0 16px 0; }
.popup-channel { display: flex; align-items: center; gap: 14px; background: #f9fafb; border-radius: 16px; padding: 12px; margin-bottom: 12px; }
.popup-channel-icon { width: 48px; height: 48px; border-radius: 50%; display: flex; align-items: center; justify-content: center; flex-shrink: 0; }
.popup-channel-icon.support { background: linear-gradient(135deg, #2563eb, #1d4ed8); color: white; font-size: 20px; }
.popup-channel-icon.sponsor { background: linear-gradient(135deg, #059669, #047857); color: white; font-size: 20px; }
.popup-channel-text { font-size: 13px; color: #374151; line-height: 1.4; }
.popup-channel-text strong { color: #1f2937; }
.popup-note { font-size: 12px; color: #6b7280; text-align: center; margin-top: 12px !important; }
.popup-footer { display: flex; justify-content: center; }
.popup-btn { padding: 12px 24px; border-radius: 40px; font-weight: 600; cursor: pointer; transition: all 0.2s; border: none; font-size: 14px; }
.popup-btn.primary { background: linear-gradient(135deg, #2563eb, #1d4ed8); color: white; box-shadow: 0 2px 8px rgba(37,99,235,0.25); }
.popup-btn.primary:hover { transform: translateY(-2px); box-shadow: 0 4px 12px rgba(37,99,235,0.3); }

/* ══════════════════════════════════════════════════════
   OVERLAY — DESKTOP + MOBILE
══════════════════════════════════════════════════════ */
.chat-window-overlay { position: fixed; bottom: 30px; right: 30px; z-index: 1002; }

/* ✅ CORRECTION MOBILE : vrai plein écran, pas de flex qui décale */
.chat-window-overlay.mobile {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: block;
  padding: 0;
  z-index: 1002;
}

/* ══════════════════════════════════════════════════════
   FENÊTRE CHAT — DESKTOP + MOBILE
══════════════════════════════════════════════════════ */
.sidebar-chat-window { background: #ffffff; border-radius: 28px; overflow: hidden; box-shadow: 0 30px 90px rgba(0,0,0,0.12), 0 0 0 1px rgba(0,0,0,0.04); border: 1px solid #e5e7eb; display: flex; width: 460px; height: 640px; max-height: calc(100vh - 80px); transition: all 0.35s cubic-bezier(0.4, 0, 0.2, 1); }
.sidebar-chat-window.chatbot-fullscreen { width: calc(100vw - 60px); height: calc(100vh - 60px); max-width: 1200px; }

/* ✅ MOBILE : colonne (main + nav en bas), plein écran sans ombre */
.sidebar-chat-window.mobile {
  width: 100%;
  height: 100%;
  max-height: 100vh;
  border-radius: 0;
  border: none;
  box-shadow: none;
  flex-direction: column;
}

/* ══════════════════════════════════════════════════════
   SIDEBAR GAUCHE (desktop uniquement)
══════════════════════════════════════════════════════ */
.chat-sidebar { width: 76px; background: #f9fafb; border-right: 1px solid #e5e7eb; display: flex; flex-direction: column; align-items: center; padding: 18px 0; gap: 8px; flex-shrink: 0; }
.sidebar-logo { width: 44px; height: 44px; border-radius: 16px; background: linear-gradient(135deg, #2563eb, #1d4ed8); display: flex; align-items: center; justify-content: center; margin-bottom: 10px; box-shadow: 0 4px 18px rgba(37, 99, 235, 0.25); }
.sidebar-logo i { color: #ffffff; font-size: 18px; }
.sidebar-divider { width: 28px; height: 1px; background: #e5e7eb; margin: 4px 0; }
.sidebar-channel-btn { width: 52px; height: 52px; border-radius: 18px; display: flex; align-items: center; justify-content: center; cursor: pointer; transition: all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1); position: relative; border: none; background: transparent; color: #6b7280; font-size: 20px; }
.sidebar-channel-btn:hover { background: #f3f4f6; color: #2563eb; transform: scale(1.08); }
.sidebar-channel-btn.active { background: linear-gradient(135deg, #2563eb, #1d4ed8); color: #ffffff; box-shadow: 0 4px 18px rgba(37, 99, 235, 0.35); transform: scale(1.05); }
.sidebar-channel-btn.active:hover { transform: scale(1.1); }
.sidebar-channel-btn.disabled { opacity: 0.25; cursor: not-allowed; }
.sidebar-channel-btn.disabled:hover { background: transparent; transform: none; color: #6b7280; }
.sidebar-channel-badge { position: absolute; top: 2px; right: 2px; background: linear-gradient(135deg, #ef4444, #dc2626); color: #ffffff; font-size: 9px; font-weight: 800; min-width: 18px; height: 18px; border-radius: 9px; display: flex; align-items: center; justify-content: center; border: 2.5px solid #f9fafb; animation: bounce-in 0.4s; }
.sidebar-channel-status { position: absolute; bottom: 4px; right: 4px; width: 12px; height: 12px; border-radius: 50%; border: 2.5px solid #f9fafb; }
.sidebar-channel-status.online { background: #22c55e; }
.sidebar-channel-status.away { background: #f59e0b; }
.sidebar-tooltip { position: absolute; left: 64px; background: #1f2937; color: #f9fafb; padding: 6px 12px; border-radius: 8px; font-size: 12px; font-weight: 500; white-space: nowrap; opacity: 0; pointer-events: none; transition: all 0.2s ease; border: 1px solid #374151; box-shadow: 0 4px 12px rgba(0,0,0,0.1); z-index: 100; }
.sidebar-channel-btn:hover .sidebar-tooltip, .sidebar-settings-btn:hover .sidebar-tooltip { opacity: 1; left: 68px; }
.sidebar-bottom { margin-top: auto; display: flex; flex-direction: column; align-items: center; gap: 6px; padding-top: 10px; }
.sidebar-settings-btn { width: 44px; height: 44px; border-radius: 14px; display: flex; align-items: center; justify-content: center; cursor: pointer; border: none; background: transparent; color: #6b7280; font-size: 16px; transition: all 0.2s ease; position: relative; }
.sidebar-settings-btn:hover { background: #f3f4f6; color: #1f2937; }

/* ══════════════════════════════════════════════════════
   CHAT MAIN AREA
══════════════════════════════════════════════════════ */
.chat-main { flex: 1; display: flex; flex-direction: column; min-width: 0; background: #ffffff; overflow: hidden; }

/* ══════════════════════════════════════════════════════
   HEADER
══════════════════════════════════════════════════════ */
.chat-header { padding: 18px 22px; border-bottom: 1px solid #e5e7eb; display: flex; align-items: center; gap: 14px; background: #f9fafb; flex-shrink: 0; }
.chat-header.sponsor-theme { background: #f0fdf4; }
.chat-header.mobile { padding: 14px 16px; gap: 10px; }
.header-back-btn { width: 36px; height: 36px; border-radius: 10px; display: flex; align-items: center; justify-content: center; cursor: pointer; border: none; background: #f3f4f6; color: #6b7280; font-size: 14px; transition: all 0.2s; flex-shrink: 0; }
.header-back-btn:hover { background: #e5e7eb; color: #1f2937; }
.chat-header-avatar { width: 44px; height: 44px; border-radius: 50%; background: linear-gradient(135deg, #2563eb, #1d4ed8); display: flex; align-items: center; justify-content: center; position: relative; flex-shrink: 0; box-shadow: 0 4px 15px rgba(37, 99, 235, 0.2); }
.chat-header-avatar.sponsor { background: linear-gradient(135deg, #059669, #047857); box-shadow: 0 4px 15px rgba(5, 150, 105, 0.2); }
.chat-header-avatar i { color: #ffffff; font-size: 18px; }
.chat-header-info { flex: 1; min-width: 0; }
.chat-header-info h4 { font-size: 15px; font-weight: 700; color: #1f2937; display: flex; align-items: center; gap: 8px; margin: 0; }
.sponsor-label { font-size: 11px; font-weight: 500; color: #059669; background: #d1fae5; padding: 2px 8px; border-radius: 6px; }
.status-online { font-size: 12px; color: #16a34a; display: flex; align-items: center; gap: 6px; margin-top: 2px; }
.status-text { font-size: 12px; display: flex; align-items: center; gap: 6px; margin-top: 2px; }
.status-text.online { color: #16a34a; }
.status-text.away { color: #f59e0b; }
.status-text.offline { color: #6b7280; }
.status-pulse { width: 8px; height: 8px; border-radius: 50%; background: #22c55e; position: relative; }
.status-pulse::after { content: ''; position: absolute; top: -2px; left: -2px; width: 12px; height: 12px; border-radius: 50%; border: 1px solid #22c55e; animation: ring-pulse 2s infinite; }
.status-pulse.away { background: #f59e0b; }
.status-pulse.away::after { border-color: #f59e0b; }
.status-pulse.offline { background: #6b7280; }
.status-pulse.offline::after { border-color: #6b7280; animation: none; }
.chat-header-actions { display: flex; gap: 6px; margin-left: auto; }
.header-action-btn { width: 36px; height: 36px; border-radius: 10px; display: flex; align-items: center; justify-content: center; cursor: pointer; border: none; background: #f3f4f6; color: #6b7280; font-size: 14px; transition: all 0.2s ease; }
.header-action-btn:hover { background: #e5e7eb; color: #1f2937; }

/* ══════════════════════════════════════════════════════
   MESSAGES
══════════════════════════════════════════════════════ */
.chat-messages { flex: 1; padding: 24px; overflow-y: auto; display: flex; flex-direction: column; gap: 18px; background: #ffffff; position: relative; }
.chat-messages::-webkit-scrollbar { width: 5px; }
.chat-messages::-webkit-scrollbar-thumb { background: #d1d5db; border-radius: 3px; }
.chat-messages::-webkit-scrollbar-track { background: transparent; }

.new-message-notification { position: sticky; top: 0; left: 24px; background: linear-gradient(135deg, #2563eb, #1d4ed8); color: #ffffff; padding: 10px 16px; border-radius: 16px; cursor: pointer; display: inline-flex; align-items: center; gap: 8px; font-size: 12px; font-weight: 600; box-shadow: 0 6px 20px rgba(37, 99, 235, 0.4); z-index: 20; transition: all 0.3s ease; margin: 12px 0; border: 1px solid rgba(255, 255, 255, 0.2); width: fit-content; }
.new-message-notification:hover { transform: scale(1.05); box-shadow: 0 8px 25px rgba(37, 99, 235, 0.5); }
.new-message-notification i { font-size: 14px; animation: bounce-in 0.5s ease; }

.scroll-to-top { position: absolute; top: 20px; left: 50%; transform: translateX(-50%); background: #2563eb; color: #ffffff; padding: 8px 20px; border-radius: 20px; cursor: pointer; display: flex; align-items: center; gap: 8px; font-size: 13px; box-shadow: 0 4px 15px rgba(37, 99, 235, 0.3); z-index: 10; border: none; transition: transform 0.2s; }
.scroll-to-top:hover { transform: translateX(-50%) scale(1.05); }

.file-drop-overlay { position: absolute; top: 0; left: 0; right: 0; bottom: 0; background: rgba(37, 99, 235, 0.08); backdrop-filter: blur(4px); z-index: 50; display: flex; align-items: center; justify-content: center; border: 2px dashed #2563eb; border-radius: 16px; margin: 16px; }
.file-drop-content { display: flex; flex-direction: column; align-items: center; gap: 12px; color: #2563eb; font-size: 16px; font-weight: 600; }
.file-drop-content i { font-size: 48px; opacity: 0.8; }

.loading-state { display: flex; flex-direction: column; align-items: center; justify-content: center; height: 100%; gap: 16px; }
.loading-spinner { width: 40px; height: 40px; border: 3px solid #e5e7eb; border-top-color: #2563eb; border-radius: 50%; animation: spin 0.8s linear infinite; }
@keyframes spin { to { transform: rotate(360deg); } }
.loading-state p { color: #6b7280; font-size: 13px; }

.empty-state { display: flex; flex-direction: column; align-items: center; justify-content: center; height: 100%; gap: 14px; padding: 24px; }
.empty-avatar { width: 72px; height: 72px; border-radius: 50%; background: linear-gradient(135deg, #2563eb, #1d4ed8); display: flex; align-items: center; justify-content: center; box-shadow: 0 8px 30px rgba(37, 99, 235, 0.15); animation: float 3s ease-in-out infinite; }
.empty-avatar.sponsor { background: linear-gradient(135deg, #059669, #047857); box-shadow: 0 8px 30px rgba(5, 150, 105, 0.15); }
@keyframes float { 0%, 100% { transform: translateY(0); } 50% { transform: translateY(-8px); } }
.empty-avatar i { color: #ffffff; }
.empty-state h3 { font-size: 17px; font-weight: 600; color: #1f2937; margin: 0; }
.empty-state p { font-size: 13px; color: #6b7280; text-align: center; margin: 0; }

.quick-suggestions { display: flex; flex-wrap: wrap; justify-content: center; gap: 10px; margin-top: 12px; }
.suggestion-chip { cursor: pointer; background: #f3f4f6; border: 1px solid #e5e7eb; border-radius: 20px; padding: 8px 16px; font-size: 12px; color: #1f2937; display: flex; align-items: center; gap: 8px; transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1); font-family: inherit; }
.suggestion-chip:hover { background: #2563eb; border-color: #2563eb; color: #ffffff; transform: translateY(-2px); box-shadow: 0 4px 15px rgba(37, 99, 235, 0.2); }
.suggestion-chip i { font-size: 12px; color: #2563eb; }
.suggestion-chip:hover i { color: #ffffff; }

.message-group { display: flex; flex-direction: column; gap: 4px; animation: messageSlide 0.35s cubic-bezier(0.4, 0, 0.2, 1); }
.message-group.message-new { animation: messageSlide 0.5s cubic-bezier(0.34, 1.56, 0.64, 1); }
@keyframes messageSlide { from { opacity: 0; transform: translateY(12px) scale(0.98); } to { opacity: 1; transform: translateY(0) scale(1); } }

.message-row { display: flex; gap: 10px; align-items: flex-end; }
.message-row.sent { flex-direction: row-reverse; }

.msg-avatar-small { width: 30px; height: 30px; border-radius: 50%; background: linear-gradient(135deg, #2563eb, #1d4ed8); display: flex; align-items: center; justify-content: center; flex-shrink: 0; }
.msg-avatar-small.sponsor { background: linear-gradient(135deg, #059669, #047857); }
.msg-avatar-small i { color: #ffffff; font-size: 11px; }

.msg-bubble { background: #f3f4f6; padding: 12px 16px; border-radius: 20px; border-bottom-left-radius: 4px; max-width: 70%; position: relative; border: 1px solid #e5e7eb; }
.message-row.sent .msg-bubble { background: linear-gradient(135deg, #2563eb, #1d4ed8); border-bottom-left-radius: 20px; border-bottom-right-radius: 4px; border: none; color: #ffffff; }
.msg-bubble.sponsor-bubble { background: #f0fdf4; border: 1px solid #bbf7d0; }

.msg-bubble-text { font-size: 13.5px; line-height: 1.55; color: #1f2937; word-wrap: break-word; }
.message-row.sent .msg-bubble-text { color: #ffffff; }

/* Product cards sent by the manager */
.msg-bubble.has-products { max-width: 88%; }
@media (max-width: 640px) { .msg-bubble.has-products { max-width: 94%; } }
.msg-products { margin-top: 8px; display: flex; flex-direction: column; gap: 8px; }
.msg-products-head { display: flex; align-items: center; gap: 7px; font-size: 11px; font-weight: 700; letter-spacing: 0.3px; text-transform: uppercase; color: #00796b; }
.msg-products-head i { font-size: 12px; }
.msg-product-share { display: flex; align-items: stretch; gap: 10px; background: #ffffff; border: 1px solid #eceff1; border-radius: 14px; overflow: hidden; cursor: pointer; box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08); transition: transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease; }
.msg-product-share:hover { transform: translateY(-2px); border-color: #00897b; box-shadow: 0 6px 18px rgba(0, 137, 123, 0.22); }
.mps-media { width: 84px; min-width: 84px; height: auto; min-height: 84px; background: linear-gradient(135deg, rgba(0, 150, 136, 0.08), rgba(0, 100, 80, 0.14)); display: flex; align-items: center; justify-content: center; color: rgba(0, 137, 123, 0.35); font-size: 30px; }
.mps-media img { width: 100%; height: 100%; object-fit: cover; }
.mps-media-ph { font-size: 30px; font-weight: 900; color: #00897b; text-transform: uppercase; letter-spacing: 1px; }
.mps-info { flex: 1; min-width: 0; padding: 9px 10px 9px 0; display: flex; flex-direction: column; gap: 3px; }
.mps-name { font-size: 13px; font-weight: 800; color: #263238; line-height: 1.25; display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden; }
.mps-price { font-size: 14px; font-weight: 900; color: #00897b; }
.mps-foot { margin-top: auto; display: flex; align-items: center; justify-content: space-between; gap: 6px; }
.mps-cat { font-size: 10.5px; font-weight: 700; color: rgba(0, 0, 0, 0.45); text-transform: capitalize; display: inline-flex; align-items: center; gap: 4px; min-width: 0; }
.mps-cat i { font-size: 9px; }
.mps-cta { font-size: 11px; font-weight: 800; color: #00796b; white-space: nowrap; display: inline-flex; align-items: center; gap: 4px; }
.mps-cta i { font-size: 10px; transition: transform 0.2s ease; }
.msg-product-share:hover .mps-cta i { transform: translateX(3px); }
.msg-all-products-btn { width: 100%; padding: 8px; background: rgba(0, 137, 123, 0.1); border: 1px dashed rgba(0, 137, 123, 0.45); border-radius: 12px; color: #00796b; font-size: 12px; font-weight: 800; cursor: pointer; display: flex; align-items: center; justify-content: center; gap: 7px; transition: all 0.2s ease; }
.msg-all-products-btn:hover { background: rgba(0, 137, 123, 0.18); }
.message-row.sent .msg-products-head { color: rgba(255, 255, 255, 0.92); }
.message-row.sent .msg-product-share { background: #fff; border-color: rgba(255, 255, 255, 0.6); }
.message-row.sent .msg-all-products-btn { background: rgba(255, 255, 255, 0.2); border-color: rgba(255, 255, 255, 0.6); color: #ffffff; }
@media (max-width: 640px) { .mps-media { width: 68px; min-width: 68px; min-height: 68px; font-size: 24px; } }

.msg-bubble-meta { display: flex; align-items: center; justify-content: flex-end; gap: 5px; margin-top: 6px; font-size: 10px; color: #9ca3af; }
.message-row.sent .msg-bubble-meta { color: rgba(255,255,255,0.7); }
.msg-bubble-meta i.read { color: #2563eb; }
.message-row.sent .msg-bubble-meta i.read { color: rgba(255,255,255,0.9); }

.message-reaction-popup { position: absolute; bottom: 100%; left: 50%; transform: translateX(-50%); background: #ffffff; border: 1px solid #e5e7eb; border-radius: 20px; padding: 6px 10px; display: flex; gap: 4px; box-shadow: 0 8px 24px rgba(0,0,0,0.08); z-index: 10; margin-bottom: 8px; }
.message-reaction-popup button { background: transparent; border: none; font-size: 18px; cursor: pointer; padding: 4px; border-radius: 50%; transition: all 0.2s; }
.message-reaction-popup button:hover { background: #f3f4f6; transform: scale(1.2); }

.date-separator { text-align: center; margin: 10px 0; position: relative; }
.date-separator::before { content: ''; position: absolute; top: 50%; left: 10%; right: 10%; height: 1px; background: #e5e7eb; }
.date-separator span { position: relative; background: #ffffff; padding: 0 16px; font-size: 11px; color: #9ca3af; font-weight: 600; letter-spacing: 0.5px; }

.media-message { max-width: 100%; }
.msg-image { width: 100%; max-width: 260px; height: auto; max-height: 300px; object-fit: cover; cursor: pointer; transition: all 0.3s ease; border-radius: 14px; display: block; }
.msg-image:hover { transform: scale(1.03); filter: brightness(0.95); }
.msg-video { width: 100%; max-width: 260px; max-height: 220px; border-radius: 14px; object-fit: cover; background: #000; display: block; }

.media-error { display: flex; flex-direction: column; align-items: center; justify-content: center; color: #9ca3af; font-size: 12px; gap: 8px; padding: 24px; background: #f3f4f6; border-radius: 14px; }
.media-error i { font-size: 28px; color: #d1d5db; }
.media-caption { margin-top: 8px; font-size: 12px; color: #6b7280; }
.message-row.sent .media-caption { color: rgba(255,255,255,0.8); }

.file-message { display: flex; align-items: center; gap: 12px; padding: 12px 14px; background: #f9fafb; border-radius: 12px; cursor: pointer; transition: all 0.2s ease; }
.file-message:hover { background: #f3f4f6; }
.file-message i:first-child { color: #2563eb; font-size: 22px; }
.file-message-info { flex: 1; display: flex; flex-direction: column; min-width: 0; }
.file-message-name { font-size: 12px; font-weight: 500; color: #1f2937; word-break: break-all; }
.file-message-size { font-size: 10px; color: #6b7280; }
.file-message-download { color: #9ca3af; font-size: 13px; }

.typing-bubble { background: #f3f4f6; padding: 14px 18px; border: 1px solid #e5e7eb; }
.typing-indicator { display: flex; align-items: center; gap: 5px; padding: 2px 4px; }
.typing-dot { width: 7px; height: 7px; border-radius: 50%; background: #2563eb; animation: typingBounce 1.4s infinite ease-in-out; }
.typing-dot:nth-child(2) { animation-delay: 0.2s; }
.typing-dot:nth-child(3) { animation-delay: 0.4s; }
@keyframes typingBounce { 0%, 60%, 100% { transform: translateY(0); opacity: 0.4; } 30% { transform: translateY(-5px); opacity: 1; } }

.scroll-to-bottom { position: absolute; bottom: 24px; right: 24px; background: #2563eb; color: #ffffff; border-radius: 50%; width: 44px; height: 44px; cursor: pointer; display: flex; align-items: center; justify-content: center; box-shadow: 0 4px 20px rgba(37, 99, 235, 0.3); z-index: 10; transition: all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1); font-size: 14px; border: none; }
.scroll-to-bottom:hover { transform: scale(1.15); box-shadow: 0 6px 24px rgba(37, 99, 235, 0.4); }
.scroll-badge { position: absolute; top: -4px; right: -4px; background: linear-gradient(135deg, #ef4444, #dc2626); color: #ffffff; font-size: 10px; font-weight: 800; min-width: 18px; height: 18px; border-radius: 9px; display: flex; align-items: center; justify-content: center; border: 2px solid #ffffff; }

/* ══════════════════════════════════════════════════════
   FILE PREVIEW
══════════════════════════════════════════════════════ */
.file-preview-bar { padding: 14px 18px; border-top: 1px solid #e5e7eb; background: #f9fafb; display: flex; align-items: center; gap: 14px; flex-shrink: 0; }
.file-preview-content { flex: 1; display: flex; align-items: center; gap: 12px; min-width: 0; }
.file-preview-thumb { width: 44px; height: 44px; border-radius: 10px; object-fit: cover; }
.file-preview-icon { width: 44px; height: 44px; border-radius: 10px; background: #f3f4f6; display: flex; align-items: center; justify-content: center; color: #2563eb; font-size: 20px; flex-shrink: 0; }
.file-preview-info { display: flex; flex-direction: column; min-width: 0; }
.file-preview-name { font-size: 12px; font-weight: 500; color: #1f2937; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.file-preview-size { font-size: 11px; color: #6b7280; }
.file-preview-remove { width: 32px; height: 32px; border-radius: 50%; border: none; background: #f3f4f6; color: #9ca3af; cursor: pointer; display: flex; align-items: center; justify-content: center; transition: all 0.2s ease; flex-shrink: 0; }
.file-preview-remove:hover { background: #e5e7eb; color: #1f2937; }

/* ══════════════════════════════════════════════════════
   INPUT AREA
══════════════════════════════════════════════════════ */
.chat-input-area { padding: 12px 10px; border-top: 1px solid #e5e7eb; display: flex; gap: 8px; align-items: flex-end; background: #ffffff; flex-shrink: 0; overflow: visible; }
.chat-input-area.mobile { padding: 10px 8px; gap: 6px; }

.chat-input { flex: 1; background: transparent; border: none; color: #1f2937; font-size: 14px; font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif; outline: none; padding: 10px 0; min-height: 20px; max-height: 100px; resize: none; line-height: 1.5; }
.chat-input::placeholder { color: #9ca3af; }

.input-action-btn { width: 34px; height: 34px; border-radius: 50%; display: flex; align-items: center; justify-content: center; cursor: pointer; border: none; background: transparent; color: #6b7280; font-size: 15px; transition: all 0.2s ease; flex-shrink: 0; margin-bottom: 2px; }
.input-action-btn:hover { background: #f3f4f6; color: #1f2937; }
.input-action-btn.attach-btn i.rotate { transform: rotate(45deg); transition: transform 0.3s ease; }

.send-btn-inline { width: 40px; height: 40px; min-width: 40px; border-radius: 50%; background: #e5e7eb; border: none; color: #9ca3af; cursor: pointer; display: flex; align-items: center; justify-content: center; transition: all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1); flex-shrink: 0; font-size: 15px; position: relative; margin-bottom: 2px; }
.send-btn-inline.has-text { background: linear-gradient(135deg, #2563eb, #1d4ed8); color: #ffffff; box-shadow: 0 4px 12px rgba(37, 99, 235, 0.25); }
.send-btn-inline.has-text:hover { transform: scale(1.1); box-shadow: 0 6px 16px rgba(37, 99, 235, 0.3); }
.send-btn-inline.has-text:active { transform: scale(0.95); }
.send-btn-inline:disabled { opacity: 0.5; cursor: not-allowed; transform: none; }
.send-btn-inline.sponsor-send.has-text { background: linear-gradient(135deg, #059669, #047857); box-shadow: 0 4px 12px rgba(5, 150, 105, 0.25); }
.send-btn-inline.sponsor-send.has-text:hover { box-shadow: 0 6px 16px rgba(5, 150, 105, 0.3); }
.send-spinner { width: 16px; height: 16px; border: 2px solid rgba(255,255,255,0.3); border-top-color: #ffffff; border-radius: 50%; animation: spin 0.6s linear infinite; }

.attachment-panel { padding: 16px; background: #ffffff; border-top: 1px solid #e5e7eb; flex-shrink: 0; }
.attachment-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 12px; }
.attachment-item { display: flex; flex-direction: column; align-items: center; gap: 8px; padding: 14px 8px; background: #f9fafb; border: 1px solid #e5e7eb; border-radius: 16px; cursor: pointer; transition: all 0.25s ease; color: #1f2937; font-size: 12px; font-family: inherit; }
.attachment-item:hover { background: #f3f4f6; transform: translateY(-3px); }
.attachment-icon { width: 44px; height: 44px; border-radius: 14px; display: flex; align-items: center; justify-content: center; font-size: 20px; }
.attachment-icon.image-icon { background: #eff6ff; color: #2563eb; }
.attachment-icon.video-icon { background: #fff7ed; color: #f59e0b; }
.attachment-icon.file-icon { background: #f0fdf4; color: #10b981; }
.attachment-icon.emoji-icon { background: #fdf2f8; color: #ec4899; }

.emoji-picker-panel { padding: 14px; background: #ffffff; border-top: 1px solid #e5e7eb; max-height: 220px; overflow-y: auto; flex-shrink: 0; }
.emoji-grid { display: flex; flex-wrap: wrap; gap: 6px; }
.emoji-btn { width: 40px; height: 40px; border-radius: 10px; border: none; background: transparent; cursor: pointer; font-size: 20px; transition: all 0.2s ease; display: flex; align-items: center; justify-content: center; }
.emoji-btn:hover { background: #f3f4f6; transform: scale(1.25); }

/* ══════════════════════════════════════════════════════
   ✅ BARRE DE NAVIGATION MOBILE
══════════════════════════════════════════════════════ */
.mobile-bottom-nav {
  display: flex;
  flex-shrink: 0;
  background: #ffffff;
  border-top: 1px solid #e5e7eb;
  padding: 6px 0;
  padding-bottom: calc(6px + env(safe-area-inset-bottom));
}

.mobile-nav-btn {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 3px;
  padding: 8px 12px 6px;
  border: none;
  background: transparent;
  color: #9ca3af;
  cursor: pointer;
  position: relative;
  transition: color 0.2s ease;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
}

.mobile-nav-btn.active { color: #2563eb; }

.mobile-nav-btn.disabled { opacity: 0.3; cursor: not-allowed; }

.mobile-nav-btn i { font-size: 22px; }

.mobile-nav-label {
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 0.3px;
}

/* Trait actif en haut du bouton */
.mobile-nav-indicator {
  position: absolute;
  top: 0;
  left: 50%;
  transform: translateX(-50%);
  width: 28px;
  height: 3px;
  background: #2563eb;
  border-radius: 0 0 4px 4px;
}

.mobile-nav-badge {
  position: absolute;
  top: 4px;
  left: calc(50% + 8px);
  background: linear-gradient(135deg, #ef4444, #dc2626);
  color: #ffffff;
  font-size: 9px;
  font-weight: 800;
  min-width: 16px;
  height: 16px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 2px solid #ffffff;
}

.mobile-nav-status {
  position: absolute;
  top: 6px;
  left: calc(50% + 10px);
  width: 10px;
  height: 10px;
  border-radius: 50%;
  border: 2px solid #ffffff;
}
.mobile-nav-status.online  { background: #22c55e; }
.mobile-nav-status.away    { background: #f59e0b; }
.mobile-nav-status.offline { background: #9ca3af; }

/* ══════════════════════════════════════════════════════
   LIENS DANS LES MESSAGES
══════════════════════════════════════════════════════ */
.msg-bubble-text >>> .message-link { color: #2563eb; text-decoration: underline; text-underline-offset: 2px; }
.message-row.sent .msg-bubble-text >>> .message-link { color: #ffffff; }

/* ══════════════════════════════════════════════════════
   IMAGE MODAL
══════════════════════════════════════════════════════ */
.custom-image-modal { position: fixed; top: 0; left: 0; right: 0; bottom: 0; background: rgba(0, 0, 0, 0.85); backdrop-filter: blur(8px); display: flex; align-items: center; justify-content: center; z-index: 10000; cursor: pointer; }
.custom-image-modal-content { position: relative; max-width: 90vw; max-height: 90vh; background: transparent; border-radius: 12px; overflow: hidden; cursor: default; }
.custom-image-modal-content img { width: 100%; height: auto; max-height: 90vh; object-fit: contain; display: block; }
.custom-close-btn { position: absolute; top: 16px; right: 16px; width: 40px; height: 40px; border-radius: 50%; background: rgba(0, 0, 0, 0.6); border: none; color: white; font-size: 18px; cursor: pointer; display: flex; align-items: center; justify-content: center; transition: background 0.2s; z-index: 10001; }
.custom-close-btn:hover { background: rgba(0, 0, 0, 0.8); }

/* ══════════════════════════════════════════════════════
   RESPONSIVE
══════════════════════════════════════════════════════ */
@media (max-width: 768px) {
  .chat-closed-state { bottom: 20px; right: 20px; }
  .main-fab { width: 56px; height: 56px; font-size: 22px; }
  .invitation-bubble { max-width: 260px; padding: 16px 18px; }
  .msg-bubble { max-width: 80%; }
  .msg-image { max-width: 220px; }
  .msg-video { max-width: 220px; }
  .attachment-grid { grid-template-columns: repeat(2, 1fr); }
  .chat-input-area { padding: 10px 8px; gap: 6px; }
  .chat-input { padding: 8px 0; max-height: 90px; }
  .input-action-btn { width: 32px; height: 32px; font-size: 14px; margin-bottom: 1px; }
  .send-btn-inline { width: 38px; height: 38px; min-width: 38px; font-size: 14px; margin-bottom: 1px; }
  .new-message-notification { left: 16px; padding: 8px 14px; font-size: 11px; margin: 10px 0; }
  .chat-messages { padding: 16px; gap: 14px; }
  .chat-messages {
    touch-action: pan-y;
  }
}

@media (max-width: 480px) {
  .main-fab { width: 52px; height: 52px; font-size: 20px; }
  .fab-badge { min-width: 20px; height: 20px; font-size: 10px; top: -2px; right: -2px; }
  .msg-bubble { max-width: 85%; }
  .msg-image { max-width: 200px; }
  .msg-video { max-width: 200px; }
  .chat-messages { padding: 14px 12px; gap: 14px; }
  .send-btn-inline { width: 38px; height: 38px; min-width: 38px; }
  .chat-input-area { padding: 10px 8px; gap: 6px; align-items: flex-end; }
  .chat-input { padding: 10px 0; font-size: 14px; max-height: 80px; }
  .input-action-btn { width: 32px; height: 32px; font-size: 16px; margin-bottom: 2px; }
  .new-message-notification { left: 12px; padding: 8px 12px; font-size: 11px; margin: 8px 0; }
  .file-preview-bar { padding: 12px; gap: 10px; }
  .attachment-panel { padding: 14px 12px; }
  .attachment-grid { grid-template-columns: repeat(2, 1fr); gap: 10px; }
  .emoji-picker-panel { padding: 12px; max-height: 200px; }
  .emoji-grid { gap: 4px; }
  .emoji-btn { width: 36px; height: 36px; font-size: 18px; }
  .mobile-nav-btn i { font-size: 20px; }
}

@media (max-width: 380px) {
  .main-fab { width: 48px; height: 48px; font-size: 18px; }
  .send-btn-inline { width: 36px; height: 36px; min-width: 36px; font-size: 12px; }
  .chat-input-area { padding: 8px 6px; gap: 4px; }
  .input-action-btn { width: 30px; height: 30px; font-size: 12px; }
  .chat-input { padding: 6px 0; font-size: 13px; max-height: 70px; }
  .msg-bubble { max-width: 90%; padding: 10px 12px; }
  .msg-bubble-text { font-size: 12px; }
  .new-message-notification { left: 10px; right: 10px; width: auto; padding: 6px 10px; font-size: 10px; gap: 6px; }
  .mobile-nav-btn { padding: 6px 8px 4px; }
  .mobile-nav-label { font-size: 9px; }
  .mobile-nav-btn i { font-size: 19px; }
}

@media (max-height: 500px) and (max-width: 768px) {
  .sidebar-chat-window { height: 100vh; }
  .chat-header { padding: 10px 14px; }
  .chat-input-area { padding: 8px 12px; gap: 8px; }
  .chat-messages { padding: 10px 12px; gap: 10px; }
  .mobile-bottom-nav { padding: 4px 0; padding-bottom: calc(4px + env(safe-area-inset-bottom)); }
  .mobile-nav-label { display: none; }
  .mobile-nav-btn { padding: 6px 12px; }
}/* SCROLL FIX MOBILE */
.chat-messages {
  -webkit-overflow-scrolling: touch;
  overscroll-behavior: contain;
}
</style>





